import React, { useEffect, useState } from 'react'
import type { CrossSectionTemplateResponse } from '@structura/beam-analysis'
import { useTranslation } from '@structura/i18n'

interface GeometryGroups {
  [material: string]: CrossSectionTemplateResponse[]
}

export interface GeometrySectionProps {
  templateMaterialType: string
  getSessionHeaders: () => HeadersInit
}

const GeometrySection: React.FC<GeometrySectionProps> = ({ templateMaterialType, getSessionHeaders }) => {
  const { t } = useTranslation()
  const [geometryLoading, setGeometryLoading] = useState<boolean>(false)
  const [geometryError, setGeometryError] = useState<string | null>(null)
  const [geometryGroups, setGeometryGroups] = useState<GeometryGroups>({})
  const [selectedGeometry, setSelectedGeometry] = useState<CrossSectionTemplateResponse | null>(null)

  useEffect(() => {
    const loadGeometryTemplates = async (): Promise<void> => {
      setGeometryLoading(true)
      setGeometryError(null)
      try {
        const response = await fetch(`/api/CrossSection/templates?materialType=${encodeURIComponent(templateMaterialType)}`, {
          headers: getSessionHeaders()
        })

        if (!response.ok) {
          throw new Error(t('admin.geometry.errLoadHttp', { status: response.status }))
        }

        const templates: CrossSectionTemplateResponse[] = await response.json()
        const groups: GeometryGroups = {}
        for (let index = 0; index < templates.length; index++) {
          const template = templates[index]
          const key = template.materialType.toLowerCase()
          if (!groups[key]) {
            groups[key] = []
          }
          groups[key].push(template)
        }

        setGeometryGroups(groups)
      } catch (err) {
        const message = (err as Error).message || t('admin.geometry.errLoad')
        setGeometryError(message)
      } finally {
        setGeometryLoading(false)
      }
    }

    void loadGeometryTemplates()
  }, [templateMaterialType, getSessionHeaders, t])

  return (
    <>
      <div className="card">
        <div className="card-header">
          <h5 className="mb-0">
            <i className="bi bi-rulers me-2"></i>
            {t('admin.geometry.title')}
          </h5>
        </div>
        <div className="card-body">
          <p className="text-muted">
            {t('admin.geometry.introBefore')} <code>Geometry.db</code>{t('admin.geometry.introAfter')}
          </p>

          {geometryLoading && (
            <div className="d-flex align-items-center mb-3">
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              <span>{t('admin.geometry.loading')}</span>
            </div>
          )}

          {geometryError && (
            <div className="alert alert-danger alert-dismissible fade show" role="alert">
              {geometryError}
              <button type="button" className="btn-close" onClick={() => setGeometryError(null)}></button>
            </div>
          )}

          {!geometryLoading && !geometryError && (
            <div className="mt-3">
              <div className="row g-3">
                {['steel', 'concrete', 'timber'].map((materialKey) => {
                  const items = geometryGroups[materialKey] || []
                  if (items.length === 0) {
                    return null
                  }

                  const title =
                    materialKey === 'steel' ? t('admin.geometry.steelSections') : materialKey === 'concrete' ? t('admin.geometry.concreteSections') : t('admin.geometry.timberSections')

                  return (
                    <div key={materialKey} className="col-12">
                      <div className="card border-secondary">
                        <div className="card-header bg-light">
                          <h6 className="mb-0">
                            <i className="bi bi-box-seam me-2"></i>
                            {title}
                          </h6>
                        </div>
                        <div className="card-body p-0">
                          <div className="table-responsive">
                            <table className="table table-sm mb-0">
                              <thead>
                                <tr>
                                  <th>{t('admin.geometry.thName')}</th>
                                  <th>{t('admin.geometry.thType')}</th>
                                  <th className="text-end">h [mm]</th>
                                  <th className="text-end">b [mm]</th>
                                  <th className="text-end">A [m²]</th>
                                  <th className="text-end">Iy [m⁴]</th>
                                  <th className="text-end">Iz [m⁴]</th>
                                </tr>
                              </thead>
                              <tbody>
                                {items.map((section) => (
                                  <tr key={section.id} style={{ cursor: 'pointer' }} onClick={() => setSelectedGeometry(section)}>
                                    <td>
                                      <strong className="text-primary">
                                        <i className="bi bi-info-circle me-1"></i>
                                        {section.name}
                                      </strong>
                                    </td>
                                    <td className="text-capitalize">{section.sectionType}</td>
                                    <td className="text-end">{section.heightMillimeters.toFixed(1)}</td>
                                    <td className="text-end">{section.widthMillimeters.toFixed(1)}</td>
                                    <td className="text-end">{section.areaSquareMeters.toExponential(3)}</td>
                                    <td className="text-end">{section.inertiaStrongAxisMeters4.toExponential(3)}</td>
                                    <td className="text-end">{section.inertiaWeakAxisMeters4.toExponential(3)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                })}

                {Object.keys(geometryGroups).length === 0 && (
                  <div className="col-12">
                    <div className="alert alert-info mb-0">
                      <i className="bi bi-info-circle me-2"></i>
                      {t('admin.geometry.noTemplatesBefore')} <code>Geometry.db</code>{t('admin.geometry.noTemplatesAfter')}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Geometry Details Modal */}
      {selectedGeometry && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} onClick={() => setSelectedGeometry(null)}>
          <div className="modal-dialog modal-lg modal-dialog-scrollable" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-rulers me-2"></i>
                  {selectedGeometry.name}
                </h5>
                <button type="button" className="btn-close" onClick={() => setSelectedGeometry(null)}></button>
              </div>
              <div className="modal-body">
                <div className="row mb-3">
                  <div className="col-md-8">
                    <span className="badge bg-primary me-2 text-capitalize">{selectedGeometry.materialType}</span>
                    <span className="badge bg-info text-capitalize">{selectedGeometry.sectionType}</span>
                  </div>
                  <div className="col-md-4 text-end">
                    {selectedGeometry.isTemplate && (
                      <span className="badge bg-success">{t('admin.geometry.badgeTemplate')}</span>
                    )}
                  </div>
                </div>

                <div className="row g-3">
                  <div className="col-12">
                    <h6 className="border-bottom pb-2">{t('admin.geometry.geometricProperties')}</h6>
                  </div>

                  <div className="col-md-6">
                    <label className="small text-muted">{t('admin.geometry.heightH')}</label>
                    <div className="fw-bold">{selectedGeometry.heightMillimeters.toFixed(1)} mm</div>
                  </div>

                  <div className="col-md-6">
                    <label className="small text-muted">{t('admin.geometry.widthB')}</label>
                    <div className="fw-bold">{selectedGeometry.widthMillimeters.toFixed(1)} mm</div>
                  </div>

                  {selectedGeometry.webThicknessMillimeters > 0 && (
                    <div className="col-md-6">
                      <label className="small text-muted">{t('admin.geometry.webThicknessTw')}</label>
                      <div className="fw-bold">{selectedGeometry.webThicknessMillimeters.toFixed(1)} mm</div>
                    </div>
                  )}

                  {selectedGeometry.flangeThicknessMillimeters > 0 && (
                    <div className="col-md-6">
                      <label className="small text-muted">{t('admin.geometry.flangeThicknessTf')}</label>
                      <div className="fw-bold">{selectedGeometry.flangeThicknessMillimeters.toFixed(1)} mm</div>
                    </div>
                  )}

                  <div className="col-12 mt-4">
                    <h6 className="border-bottom pb-2">{t('admin.geometry.sectionProperties')}</h6>
                  </div>

                  <div className="col-md-6">
                    <label className="small text-muted">{t('admin.geometry.areaA')}</label>
                    <div className="fw-bold">{selectedGeometry.areaSquareMeters.toExponential(3)} m²</div>
                    <div className="small text-muted">{(selectedGeometry.areaSquareMeters * 1e6).toFixed(1)} mm²</div>
                  </div>

                  <div className="col-md-6">
                    <label className="small text-muted">{t('admin.geometry.momentInertiaIy')}</label>
                    <div className="fw-bold">{selectedGeometry.inertiaStrongAxisMeters4.toExponential(3)} m⁴</div>
                    <div className="small text-muted">{(selectedGeometry.inertiaStrongAxisMeters4 * 1e8).toFixed(1)} cm⁴</div>
                  </div>

                  <div className="col-md-6">
                    <label className="small text-muted">{t('admin.geometry.momentInertiaIz')}</label>
                    <div className="fw-bold">{selectedGeometry.inertiaWeakAxisMeters4.toExponential(3)} m⁴</div>
                    <div className="small text-muted">{(selectedGeometry.inertiaWeakAxisMeters4 * 1e8).toFixed(1)} cm⁴</div>
                  </div>

                  {selectedGeometry.elasticModulusYMillimeters3 && selectedGeometry.elasticModulusYMillimeters3 > 0 && (
                    <div className="col-md-6">
                      <label className="small text-muted">{t('admin.geometry.elasticModulusWely')}</label>
                      <div className="fw-bold">{selectedGeometry.elasticModulusYMillimeters3.toExponential(2)} mm³</div>
                    </div>
                  )}

                  {selectedGeometry.plasticModulusYMillimeters3 && selectedGeometry.plasticModulusYMillimeters3 > 0 && (
                    <div className="col-md-6">
                      <label className="small text-muted">{t('admin.geometry.plasticModulusWply')}</label>
                      <div className="fw-bold">{selectedGeometry.plasticModulusYMillimeters3.toExponential(2)} mm³</div>
                    </div>
                  )}

                  {selectedGeometry.sectionClass && (
                    <div className="col-md-6">
                      <label className="small text-muted">{t('admin.geometry.sectionClass')}</label>
                      <div className="fw-bold">{selectedGeometry.sectionClass}</div>
                    </div>
                  )}

                  {/* Moment Capacities */}
                  {(selectedGeometry.elasticMomentCapacityKNm || selectedGeometry.plasticMomentCapacityKNm || selectedGeometry.reinforcedMomentCapacityKNm) && (
                    <>
                      <div className="col-12 mt-4">
                        <h6 className="border-bottom pb-2">{t('admin.geometry.momentCapacity')}</h6>
                      </div>

                      {selectedGeometry.elasticMomentCapacityKNm && selectedGeometry.elasticMomentCapacityKNm > 0 && (
                        <div className="col-md-6">
                          <label className="small text-muted">{t('admin.geometry.elasticCapacityMel')}</label>
                          <div className="fw-bold">{selectedGeometry.elasticMomentCapacityKNm.toFixed(2)} kNm</div>
                        </div>
                      )}

                      {selectedGeometry.plasticMomentCapacityKNm && selectedGeometry.plasticMomentCapacityKNm > 0 && (
                        <div className="col-md-6">
                          <label className="small text-muted">{t('admin.geometry.plasticCapacityMpl')}</label>
                          <div className="fw-bold">{selectedGeometry.plasticMomentCapacityKNm.toFixed(2)} kNm</div>
                        </div>
                      )}

                      {selectedGeometry.reinforcedMomentCapacityKNm && selectedGeometry.reinforcedMomentCapacityKNm > 0 && (
                        <div className="col-md-6">
                          <label className="small text-muted">{t('admin.geometry.reinforcedCapacityMRd')}</label>
                          <div className="fw-bold">{selectedGeometry.reinforcedMomentCapacityKNm.toFixed(2)} kNm</div>
                        </div>
                      )}
                    </>
                  )}

                  {/* Concrete reinforcement info */}
                  {selectedGeometry.materialType.toLowerCase() === 'concrete' && (
                    <>
                      {(selectedGeometry.reinforcementAreaBottomSquareMeters || selectedGeometry.effectiveCoverMillimeters || selectedGeometry.effectiveDepthMillimeters) && (
                        <>
                          <div className="col-12 mt-4">
                            <h6 className="border-bottom pb-2">{t('admin.geometry.reinforcementProperties')}</h6>
                          </div>

                          {selectedGeometry.reinforcementAreaBottomSquareMeters && selectedGeometry.reinforcementAreaBottomSquareMeters > 0 && (
                            <div className="col-md-6">
                              <label className="small text-muted">{t('admin.geometry.bottomReinfArea')}</label>
                              <div className="fw-bold">{(selectedGeometry.reinforcementAreaBottomSquareMeters * 1e6).toFixed(0)} mm²</div>
                            </div>
                          )}

                          {selectedGeometry.effectiveCoverMillimeters && selectedGeometry.effectiveCoverMillimeters > 0 && (
                            <div className="col-md-6">
                              <label className="small text-muted">{t('admin.geometry.effectiveCover')}</label>
                              <div className="fw-bold">{selectedGeometry.effectiveCoverMillimeters.toFixed(0)} mm</div>
                            </div>
                          )}

                          {selectedGeometry.effectiveDepthMillimeters && selectedGeometry.effectiveDepthMillimeters > 0 && (
                            <div className="col-md-6">
                              <label className="small text-muted">{t('admin.geometry.effectiveDepthD')}</label>
                              <div className="fw-bold">{selectedGeometry.effectiveDepthMillimeters.toFixed(0)} mm</div>
                            </div>
                          )}

                          {selectedGeometry.reinforcementRatio && selectedGeometry.reinforcementRatio > 0 && (
                            <div className="col-md-6">
                              <label className="small text-muted">{t('admin.geometry.reinforcementRatioRho')}</label>
                              <div className="fw-bold">{(selectedGeometry.reinforcementRatio * 100).toFixed(2)} %</div>
                            </div>
                          )}
                        </>
                      )}
                    </>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setSelectedGeometry(null)}>
                  {t('beam.common.close')}
                </button>
                <button type="button" className="btn btn-primary" disabled>
                  <i className="bi bi-pencil me-1"></i>
                  {t('admin.common.editComingSoon')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default GeometrySection
